import type {
  QueryableSelectFilterFn,
  QueryableSelectOptionItem,
  QueryableSelectProps,
} from "./queryable-select.type";

import { useDebounce } from "@react-hook/debounce";
import { Form, Select, Spin } from "antd";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useList } from "@/hooks/queries";
import { getSelectDropdownRender } from "@/ui/select-dropdown";
import { toSnakeCase } from "@/utilities/string";

import "./queryable-select.style.less";

// TODO DEV-8551 `Option` & `OptGroup` are deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { OptGroup, Option } = Select;

const QueryableSelect = <Value = unknown, DataItem = unknown>({
  model,
  filterBy,
  sortBy = "-updated_at",
  tableName,
  mode,
  dataTestId,
  optionFormat,
  groups,
  groupEmptyText,
  name,
  label,
  rules = [],
  placeholder,
  emptyPlaceholder,
  parentLoading = false,
  validateStatus,
  help,
  formatFieldValue,
  additionalFilters,
  onChange,
  disabled,
  disabledOnEmpty,
  initialValue,
  pageSize = 15,
  fields,
  onOptionSelected,
  selectStyle,
  formItemStyle,
  presetModelOptions,
  rootId = null,
  hiddenIfEmpty = false,
  filterCallback,
  allowClear = true,
  dropdownRenderOptions,
  className,
  ...othersSelectProps
  // TODO DEV-8409 reduce complexity
  // eslint-disable-next-line complexity
}: QueryableSelectProps<Value, DataItem>) => {
  const { t } = useTranslation();

  const enableQuery = !presetModelOptions && !disabled;

  const [isFocused, setIsFocused] = useState(false);
  const [debouncedSearchField, setSearchFieldDebounced] = useDebounce<
    string | undefined
  >(undefined, 500);

  const fieldsTableName = tableName ?? toSnakeCase(model);
  const fieldsParam = fields &&
    fields.length > 0 && {
      [`fields[${fieldsTableName}]`]: fields.join(","),
    };

  const filterParam = debouncedSearchField
    ? { [`filter[${filterBy}]`]: debouncedSearchField }
    : {};

  const { data, isLoading } = useList<DataItem>(model, {
    params: {
      sort: sortBy,
      [`page[size]`]: pageSize,
      ...filterParam,
      ...fieldsParam,
      ...additionalFilters,
    },
    enabled: enableQuery,
    neverStale: true,
    rootId,
  });

  const loadedData = data?.data ?? [];
  const isEmpty = !isLoading && !loadedData.length;

  const handleSearch = (value: string) => {
    setSearchFieldDebounced(value);
  };

  const handleChange = (
    option:
      | QueryableSelectOptionItem<Value>
      | QueryableSelectOptionItem<Value>[]
  ) => {
    if (onChange) {
      let value = undefined;
      if (option && "value" in option) value = option.value;
      else if (Array.isArray(option)) value = option.map((o) => o.value);
      onChange(value);
    }

    if (onOptionSelected) {
      const selectedValue = option && "value" in option ? option.value : option;
      const dataItem = selectOptions?.find(
        (item) => (item as Record<string, unknown>).id === selectedValue
      );
      onOptionSelected(option, dataItem);
    }
  };

  const genericSelectProps = {
    ...othersSelectProps,
    disabled: isEmpty && disabledOnEmpty ? true : disabled,
    labelInValue: true,
    allowClear: allowClear,
    showSearch: isFocused ? { onSearch: handleSearch } : false,
    filterOption: false,
    defaultActiveFirstOption: false,
    notFoundContent: null,
    isqueryableselect: "true", // for testing
    popupRender: getSelectDropdownRender(dropdownRenderOptions),
    className: classNames("queryable-select", className),
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      setIsFocused(false);
      setSearchFieldDebounced(undefined);
    },
  };

  const renderFormItem = () => (
    <Form.Item
      name={name}
      label={label}
      validateStatus={validateStatus}
      help={help}
      initialValue={initialValue}
      getValueProps={(fieldValue) => ({
        value: formatFieldValue ? formatFieldValue(fieldValue) : fieldValue,
      })}
      rules={rules}
      style={formItemStyle}
    >
      {renderSelectItem()}
    </Form.Item>
  );

  const filteredLoadedData = loadedData.filter(
    filterCallback ? filterCallback : () => true
  );

  const selectOptions = presetModelOptions?.length
    ? presetModelOptions.concat(filteredLoadedData)
    : filteredLoadedData;

  const renderSelectItem = () => (
    <Select
      data-testid={dataTestId}
      placeholder={isEmpty ? (emptyPlaceholder ?? placeholder) : placeholder}
      mode={mode}
      onChange={handleChange}
      style={selectStyle ? selectStyle : undefined}
      {...genericSelectProps}
    >
      {renderOptionsItems()}
    </Select>
  );

  const renderOptionsItems = () => {
    if (!selectOptions?.length) return;

    if (!groups?.length) {
      return selectOptions.map(renderOptionItem);
    }

    return groups.map(({ label, filterFn, emptyText }) => (
      <OptGroup label={label} key={label}>
        {renderGroupOptionsItems(filterFn, emptyText)}
      </OptGroup>
    ));
  };

  const renderGroupOptionsItems = (
    filterFn: QueryableSelectFilterFn<DataItem>,
    emptyText?: string
  ) => {
    const groupData = selectOptions.filter(filterFn);

    if (!groupData.length) {
      return (
        <Option disabled style={{ fontStyle: "italic" }}>
          {emptyText ?? groupEmptyText ?? t("global:noData")}
        </Option>
      );
    }

    return groupData.map(renderOptionItem);
  };

  const renderOptionItem = (option: DataItem) => {
    return optionFormat({ option, fieldName: name });
  };

  const render = () => {
    if (hiddenIfEmpty && loadedData?.length === 0) {
      return <></>;
    }

    return renderFormItem();
  };

  return (
    <Spin spinning={isLoading || parentLoading}>
      {!parentLoading && render()}
    </Spin>
  );
};

export default QueryableSelect;
